import React, { useState } from 'react';
import { Link } from 'react-router-dom'; // Added Link import

const Tracking: React.FC = () => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleTracking = (e: React.FormEvent) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;

    setIsLoading(true);
    
    // Simulación de búsqueda de seguimiento
    setTimeout(() => {
      const mockResult = {
        trackingNumber: trackingNumber,
        status: 'En Tránsito',
        origin: 'Ciudad de Origen',
        destination: 'Ciudad de Destino',
        estimatedDelivery: '2024-01-15',
        currentLocation: 'Centro de Distribución Regional',
        timeline: [
          {
            date: '2024-01-10 08:30',
            status: 'Envío Recibido',
            location: 'Centro de Distribución Principal',
            description: 'El envío ha sido recibido y procesado'
          },
          {
            date: '2024-01-11 14:15',
            status: 'En Tránsito',
            location: 'Centro de Distribución Regional',
            description: 'El envío está siendo transportado hacia su destino'
          },
          {
            date: '2024-01-12 09:45',
            status: 'En Ruta',
            location: 'En camino',
            description: 'El vehículo está en ruta hacia el destino final'
          }
        ]
      };
      
      setTrackingResult(mockResult);
      setIsLoading(false);
    }, 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Entregado':
        return 'text-green-600 bg-green-100';
      case 'En Tránsito':
        return 'text-blue-600 bg-blue-100';
      case 'En Ruta':
        return 'text-yellow-600 bg-yellow-100';
      case 'Retrasado':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <section className="bg-primary-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-5xl font-bold mb-6">
            Seguimiento de Envíos
          </h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto">
            Rastree su envío en tiempo real con nuestro sistema avanzado de seguimiento GPS. 
            Manténgase informado sobre el estado y ubicación de su carga.
          </p>
        </div>
      </section>

      {/* Tracking Form */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Rastrear Mi Envío
            </h2>
            
            <form onSubmit={handleTracking} className="max-w-md mx-auto">
              <div className="mb-6">
                <label htmlFor="trackingNumber" className="block text-sm font-medium text-gray-700 mb-2">
                  Número de Seguimiento
                </label>
                <input
                  type="text"
                  id="trackingNumber"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Ingrese su número de seguimiento"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-primary-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Rastreando...
                  </div>
                ) : (
                  'Rastrear Envío'
                )}
              </button>
            </form>

            {/* Example Tracking Numbers */}
            <div className="mt-8 text-center">
              <p className="text-sm text-gray-600 mb-2">Números de ejemplo para probar:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {['RT123456789', 'RT987654321', 'RT456789123'].map((number) => (
                  <button
                    key={number}
                    onClick={() => setTrackingNumber(number)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    {number}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tracking Results */}
      {trackingResult && (
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  Resultado del Seguimiento
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Información del Envío</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Número de Seguimiento:</span>
                        <span className="font-medium">{trackingResult.trackingNumber}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Estado:</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trackingResult.status)}`}>
                          {trackingResult.status}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Origen:</span>
                        <span className="font-medium">{trackingResult.origin}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Destino:</span>
                        <span className="font-medium">{trackingResult.destination}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Entrega Estimada:</span>
                        <span className="font-medium">{trackingResult.estimatedDelivery}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Ubicación Actual</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-600 mb-2">El envío se encuentra en:</p>
                      <p className="font-medium text-gray-900">{trackingResult.currentLocation}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">Historial del Envío</h3>
                <div className="space-y-4">
                  {trackingResult.timeline.map((event: any, index: number) => (
                    <div key={index} className="flex items-start">
                      <div className="flex-shrink-0 w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center mr-4">
                        <svg className="w-4 h-4 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-gray-900">{event.status}</h4>
                          <span className="text-sm text-gray-500">{event.date}</span>
                        </div>
                        <p className="text-sm text-gray-600 mb-1">{event.location}</p>
                        <p className="text-sm text-gray-500">{event.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Características del Seguimiento
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nuestro sistema de seguimiento le proporciona información detallada 
              y actualizada sobre su envío en tiempo real.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Seguimiento en Tiempo Real</h3>
              <p className="text-gray-600">
                Monitoree la ubicación exacta de su envío con actualizaciones en tiempo real.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4 19h6v-6H4v6zM4 5h6V4a1 1 0 00-1-1H5a1 1 0 00-1 1v1zm10 0h6V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v1z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Notificaciones Automáticas</h3>
              <p className="text-gray-600">
                Reciba notificaciones por email o SMS sobre el estado de su envío.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Reportes Detallados</h3>
              <p className="text-gray-600">
                Acceda a reportes completos del historial de su envío.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            ¿Necesita ayuda con el seguimiento?
          </h2>
          <p className="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
            Nuestro equipo de atención al cliente está disponible para ayudarle 
            con cualquier consulta sobre el seguimiento de su envío.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+15551234567"
              className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Llamar Ahora
            </a>
            <Link
              to="/contact"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors duration-200"
            >
              Contactar Soporte
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Tracking; 